import { Badge } from "@astryxdesign/core/Badge";
import { Card } from "@astryxdesign/core/Card";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { List, ListItem } from "@astryxdesign/core/List";
import { Section } from "@astryxdesign/core/Section";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { TextArea } from "@astryxdesign/core/TextArea";
import { TextInputMaybeError } from "@karrtopelka/tools-kit/TextInputMaybeError";
import { ToolShell } from "@karrtopelka/tools-kit/ToolShell";
import { useMemo, useState } from "react";

interface Hit {
  /** Full match text. */
  text: string;
  index: number;
  /** Numbered capture groups, excluding group 0. */
  groups: Array<string | undefined>;
  named: Record<string, string | undefined>;
}

type Result =
  | { ok: true; hits: Hit[] }
  | { ok: false; error: string }
  | { ok: null };

/**
 * A pathological pattern (catastrophic backtracking) can hang the tab. Matching is
 * bounded so a runaway expression fails loudly instead of freezing the page.
 */
const MATCH_LIMIT = 1000;

const SAMPLE_PATTERN = "(\\w+)@(\\w+)\\.com";
const SAMPLE_TEXT = "ping karrtopelka@example.com or hello@test.com — both should match.";

export function App() {
  const [pattern, setPattern] = useState("");
  const [text, setText] = useState("");
  const [global, setGlobal] = useState(true);
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [multiline, setMultiline] = useState(false);
  const [dotAll, setDotAll] = useState(false);

  const flags = `${global ? "g" : ""}${ignoreCase ? "i" : ""}${multiline ? "m" : ""}${dotAll ? "s" : ""}`;

  const result = useMemo<Result>(() => {
    if (pattern === "") return { ok: null };
    let re: RegExp;
    try {
      // Always compile with `g` so exec can walk the string; `global` only controls
      // whether we keep more than the first hit.
      re = new RegExp(pattern, flags.includes("g") ? flags : `${flags}g`);
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }

    const hits: Hit[] = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      hits.push({
        text: m[0],
        index: m.index,
        groups: m.slice(1),
        named: { ...m.groups },
      });
      // A zero-length match would spin forever without nudging lastIndex.
      if (m[0] === "") re.lastIndex += 1;
      if (!global) break;
      if (hits.length >= MATCH_LIMIT) break;
    }
    return { ok: true, hits };
  }, [pattern, text, flags, global]);

  /** The input text split into alternating plain / matched runs for highlighting. */
  const segments = useMemo(() => {
    if (result.ok !== true || result.hits.length === 0 || text === "") return null;
    const out: Array<{ text: string; isHit: boolean }> = [];
    let cursor = 0;
    for (const hit of result.hits) {
      if (hit.index > cursor) out.push({ text: text.slice(cursor, hit.index), isHit: false });
      out.push({ text: hit.text, isHit: true });
      cursor = hit.index + hit.text.length;
    }
    if (cursor < text.length) out.push({ text: text.slice(cursor), isHit: false });
    return out;
  }, [result, text]);

  return (
    <ToolShell active="regex" tagline="Test patterns against real text">
      <VStack gap={2}>
        <TextInputMaybeError
          label="Pattern"
          placeholder="\\w+@\\w+\\.com"
          value={pattern}
          onChange={setPattern}
          error={result.ok === false ? result.error : null}
        />

        <HStack gap={3} wrap="wrap">
          <CheckboxInput label="global (g)" value={global} onChange={setGlobal} />
          <CheckboxInput label="ignore case (i)" value={ignoreCase} onChange={setIgnoreCase} />
          <CheckboxInput label="multiline (m)" value={multiline} onChange={setMultiline} />
          <CheckboxInput label="dotall (s)" value={dotAll} onChange={setDotAll} />
        </HStack>

        <TextArea
          label="Test text"
          placeholder="Paste the text to match against"
          value={text}
          onChange={setText}
          rows={6}
          hasSpellCheck={false}
        />

        {pattern === "" && text === "" ? (
          <HStack gap={1}>
            <Text
              type="supporting"
              size="xsm"
              color="accent"
              as="label"
              onClick={() => {
                setPattern(SAMPLE_PATTERN);
                setText(SAMPLE_TEXT);
              }}
            >
              Load an example
            </Text>
          </HStack>
        ) : null}
      </VStack>

      {result.ok === true && text !== "" ? (
        <>
          <VStack gap={1}>
            <HStack gap={1} align="center">
              <Text type="label" color="secondary">
                Matches
              </Text>
              <Badge
                label={String(result.hits.length)}
                variant={result.hits.length > 0 ? "blue" : "neutral"}
              />
              {result.hits.length >= MATCH_LIMIT ? (
                <Text type="supporting" size="xsm" color="secondary">
                  stopped at {MATCH_LIMIT}
                </Text>
              ) : null}
            </HStack>

            <Card padding={3}>
              {segments ? (
                <Text type="code" size="sm">
                  {segments.map((seg, i) =>
                    seg.isHit ? (
                      // Badge carries the tinted background, so the highlight comes
                      // from the theme rather than a hand-written style.
                      <Badge key={i} variant="pink" label={seg.text} />
                    ) : (
                      <Text key={i} type="code" size="sm" color="secondary">
                        {seg.text}
                      </Text>
                    ),
                  )}
                </Text>
              ) : (
                <Text type="supporting" color="secondary">
                  No matches in this text.
                </Text>
              )}
            </Card>
          </VStack>

          {result.hits.length > 0 ? (
            <VStack gap={1}>
              <Text type="label" color="secondary">
                Detail
              </Text>
              <Section padding={0}>
                <List density="compact" hasDividers>
                  {result.hits.slice(0, 50).map((hit, i) => {
                    const captures = hit.groups
                      .map((g, gi) => `${gi + 1}: ${g ?? "—"}`)
                      .concat(
                        Object.entries(hit.named).map(([k, v]) => `${k}: ${v ?? "—"}`),
                      )
                      .join("   ");
                    return (
                      <ListItem
                        key={`${hit.index}-${i}`}
                        label={hit.text || "(empty match)"}
                        description={captures || "no capture groups"}
                        endContent={
                          <Text type="supporting" size="xsm" color="secondary">
                            @{hit.index}
                          </Text>
                        }
                      />
                    );
                  })}
                </List>
              </Section>
            </VStack>
          ) : null}
        </>
      ) : result.ok === true && text === "" ? (
        <EmptyState
          title="Add some test text"
          description="Your pattern compiles. Paste the text you want to run it against."
        />
      ) : null}

      <Text type="supporting" size="xsm" color="secondary">
        Everything runs in your browser. Nothing you type is uploaded, stored or logged.
      </Text>
    </ToolShell>
  );
}
